import * as _ from "lodash"
import * as sjcl from "sjcl"
import moment from "moment"


export const SCOPES = {
  openid: "openid",

  userUpdate: "user:update",
  userWhoami: "user:whoami",
  userChangePassword: "user:change-password",
  userCreateReferralToken: "user:create-referral-token",
  userLogout: "user:logout",

  subscriptionGet: "subscription:get",
  subscriptionList: "subscription:list",
  subscriptionCreate: "subscription:create",
  subscriptionDelete: "subscription:delete",
  subscriptionUpdate: "subscription:update",
  subscriptionCalculatePrice: "subscription:calculate-price",
  subscriptionHasPendSubscriptionPayment: "subscription:has-pend-subscription-payment",

  payerInfoGet: "payer_info:get",
  payerInfoList: "payer_info:list",
  payerInfoCreate: "payer_info:create",
  payerInfoDelete: "payer_info:delete",
  payerInfoUpdate: "payer_info:update",

  nfeInfoGet: "nfe_info:get",
  nfeInfoList: "nfe_info:list",
  nfeInfoCreate: "nfe_info:create",
  nfeInfoDelete: "nfe_info:delete",
  nfeInfoUpdate: "nfe_info:update",
}


export const getAllScopes = () => {
  return _.values(SCOPES).join(" ")
}


export class AuthError extends Error {
  constructor(errorData = undefined, ...params) {
    super(...params)

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error#Custom_Error_Types
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthError)
    }

    this.errorData = errorData
  }
}


export class BadRequest extends AuthError {
}


export class ConnectionError extends AuthError {
}


export class NoAccessTokenError extends AuthError {
}


export class LoginError extends AuthError {
}


export class TokenInvalidError extends AuthError {
}


export class TokenRefreshError extends AuthError {
}


export class IDTokenInvalidNonce extends TokenInvalidError {
}


export class TokenExpiredError extends TokenInvalidError {
}


export class InvalidSessionError extends AuthError {
}


export function parseJwt(token) {
  // Reference https://stackoverflow.com/a/38552302
  const base64Url = token.split(".")[1]
  const base64 = base64Url.replace(/-/g, "+")
    .replace(/_/g, "/")

  const jsonPayload = decodeURIComponent(
    atob(base64).split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join(""),
  )

  return JSON.parse(jsonPayload)
}


function saveToLocalStorage(key, value) {
  window.localStorage.setItem(key, value)
}


function getFromLocalStorage(key) {
  return window.localStorage.getItem(key)
}


function removeFromLocalStorage(key) {
  localStorage.removeItem(key)
}


function generateRandomString(size) {
  const stateArray = new Uint8Array(Math.ceil(size / 2))
  window.crypto.getRandomValues(stateArray)

  let values = ""
  for (const val of stateArray) {
    values += val.toString(16)
  }

  return values
}


function generateCodeChallenge(codeVerifier) {
  const codeSHA256 = sjcl.hash.sha256.hash(codeVerifier)
  let codeChallenge = sjcl.codec.base64url.fromBits(codeSHA256)
  if (codeChallenge.endsWith("=")) {
    codeChallenge = codeChallenge.substring(0, codeChallenge.length - 1)
  }

  return codeChallenge
}


function createQueryParams(queryData) {
  const queryParams = new URLSearchParams()
  for (const [key, val] of Object.entries(queryData)) {
    queryParams.append(key, val)
  }

  return queryParams
}


async function maybeParseJsonFromResponse(reponse) {
  let jsonData = null
  try {
    jsonData = await reponse.json()
  } catch (e) {
    throw new BadRequest(
      {}, "Could'nt decode jsonData from token response",
    )
  }

  return jsonData
}


function createAuthError(message, jsonData, errorClass) {
  const errorMessage = jsonData.message
  if (errorMessage) {
    message = `${message} ${errorMessage}`
  }
  return new errorClass(jsonData, message)
}


export const ACCESS_TOKEN_KEY = "at"
export const REFRESH_TOKEN_KEY = "rt"
export const ID_TOKEN_KEY = "it"

export const ACCESS_TOKEN_EXP_KEY = "atexp"

export const CODE_VERIFIER_KEY = "cvf"
export const STATE_KEY = "stv"
export const NONCE_KEY = "nnv"


export class UserData {
  constructor(
    idUserAccount,
    strName, strUsername, strEmail,
    blActive, blEmailValidated,
    blAllowAdmin, strPhoneNumber,
    abCountry, abPhoneRegion,
    dhLastLogin, dhCreated, dhUpdated,
  ) {
    this.idUserAccount = idUserAccount
    this.strUsername = strUsername
    this.strName = strName
    this.strEmail = strEmail
    this.blActive = blActive
    this.blEmailValidated = blEmailValidated
    this.blAllowAdmin = blAllowAdmin
    this.strPhoneNumber = strPhoneNumber
    this.abCountry = abCountry
    this.abPhoneRegion = abPhoneRegion
    this.dhLastLogin = dhLastLogin
    this.dhCreated = dhCreated
    this.dhUpdated = dhUpdated
  }

  static fromObject(obj) {
    const userData = new UserData()
    const availableKeys = Object.keys(userData)

    for (const key of Object.keys(obj)) {
      const pascalCaseKey = _.camelCase(key)

      if (!availableKeys.includes(pascalCaseKey)) {
        continue
      }

      userData[pascalCaseKey] = obj[key]
    }

    return userData
  }
}


export class HorusAuth {
  constructor(authServiceUrl, clientId, redirectUri) {
    this.authServiceUrl = authServiceUrl
    if (this.authServiceUrl.endsWith("/")) {
      this.authServiceUrl = this.authServiceUrl.substring(0, this.authServiceUrl.length - 1)
    }

    this.clientId = clientId
    this.redirectUri = redirectUri

    this._accessToken = null
    this._refreshToken = null
    this._idToken = null

    this.accessExpires = null

    this.onNetworkError = null
    this.onNeedAuthentication = null

    this.isSetup = false
  }

  setUp(refreshTimeInterval = 1800) {
    this.loadAuthenticationInfo().then(() => {

      this.isSetup = true
      this.startRefreshTokenJob(refreshTimeInterval)

    }).catch((err) => this.catchRequestHandler(err))
  }

  startRefreshTokenJob(timeInterval, isFastRetry = false, fastRetryTimeInterval = 5 * 60) {
    const tm = isFastRetry ? fastRetryTimeInterval : timeInterval
    setTimeout(
      () => {
        this.oauthRefreshToken()
          .then(() => {
            this.verifyAuthSession()
              .then(() => {
                this.startRefreshTokenJob(timeInterval)
              })
              .catch(() => {
                const fastRetry = 5 * 60
                this.startRefreshTokenJob(timeInterval, true, fastRetry)
              })
          })
          .catch(() => {
            // The user isn't logged in, so we will not handle any authentication
            //  logic here and let the application decide what to do.
            // We put a lower retry time to make sure that if the token
            //  is restored this job continues normally.
            const fastRetry = 5 * 60
            this.startRefreshTokenJob(timeInterval, true, fastRetry)
          })

      }, tm * 1000,
    )
  }

  afterRedirectHandler(queryObject, scopes, onSuccess, onError) {
    // Aqui é pego a resposta que veio via query parameters da tela de SSO
    if (!onSuccess) {
      onSuccess = () => {
      }  // noop
    }

    if (!onError) {
      onError = () => {
      }  // noop
    }

    // Primeiro de tudo é preciso fazer a verificação do state
    //  para ter certeza que o request é válido
    const state = queryObject["state"]
    const currentState = getFromLocalStorage(STATE_KEY)
    if (!state || currentState !== state) {
      onError("Invalid state")
      return
    }

    // Trata verifica se existe algum erro
    // eg: error=invalid_scope&state=4a843...
    const error = queryObject["error"]
    if (error) {
      onError(error)
      return
    }

    // Verificação de state foi válido, agora é a hora de pegar o token
    const code = queryObject["code"]
    this.oauthCreateToken(code, scopes).then(
      (resp) => {
        onSuccess(resp)
      },
    ).catch(
      (err) => {
        onError(err)
      },
    )
  }

  // OAuth2 Methods
  oauthAuthorizationCode(scope, actionType = "login", loginHint = "", registerKey= "") {
    const stateValue = generateRandomString(128)
    const nonceValue = generateRandomString(128)

    const codeVerifier = generateRandomString(64)
    const codeChallenge = generateCodeChallenge(codeVerifier)

    const queryParams = createQueryParams({
      "client_id": this.clientId,
      "response_type": "code",

      // "scope": scope,
      "redirect_uri": this.redirectUri,
      "state": stateValue,
      "nonce": nonceValue,

      "code_challenge": codeChallenge,
      "code_challenge_method": "S256",

      "action_type": actionType,
      "login_hint": loginHint,

      "referral_token": "",
      "register_key": registerKey
    })

    saveToLocalStorage("cvf", codeVerifier)
    saveToLocalStorage("stv", stateValue)
    saveToLocalStorage("nnv", nonceValue)

    window.location.href = `${this.authServiceUrl}/oauth/authorize/?${queryParams}`
  }

  async oauthCreateToken(code, scope, codeVerifier) {
    codeVerifier = codeVerifier || getFromLocalStorage(CODE_VERIFIER_KEY)

    const queryParams = createQueryParams({
      "client_id": this.clientId,

      "grant_type": "authorization_code",

      "code": code,
      "code_verifier": codeVerifier,
      "redirect_uri": this.redirectUri,
    })
    const tokenResponse = await this.postRequest(
      `/oauth/token/?${queryParams}`,
      {},
      false,
      "application/x-form-www-urlencoded",
    )

    return this._handleTokenResponse(tokenResponse)
  }

  async oauthRefreshToken() {
    if (!getFromLocalStorage(REFRESH_TOKEN_KEY)) {
      throw createAuthError(
        "No refresh token available",
        {message: "No refresh token available"},
        TokenInvalidError,
      )
    }

    const queryParams = createQueryParams({
      "client_id": this.clientId,
      "grant_type": "refresh_token",
      "refresh_token": getFromLocalStorage(REFRESH_TOKEN_KEY),
    })
    const refreshResponse = await this.postRequest(
      `/oauth/token/?${queryParams}`,
      {},
      false,
      "application/x-form-www-urlencoded",
    )

    return this._handleTokenResponse(refreshResponse)
  }

  async oauthIntrospectToken() {
    const queryParams = createQueryParams({
      "client_id": this.clientId,
      "token": getFromLocalStorage(ACCESS_TOKEN_KEY),
      "token_hint": "access_token",
    })
    const introspectResponse = await this.postRequest(
      `/oauth/token/introspect/?${queryParams}`,
      {},
      false,
      "application/x-form-www-urlencoded",
    )

    const jsonData = await maybeParseJsonFromResponse(introspectResponse)

    if (!introspectResponse.ok) {
      createAuthError(
        "Error introspecting token",
        jsonData,
        TokenRefreshError,
      )
    }

    const isTokensActive = jsonData.active
    if (!isTokensActive) {
      throw new TokenInvalidError(jsonData, "Token is not active")
    }

    return jsonData
  }

  async _handleTokenResponse(tokenResponse) {
    const jsonData = await maybeParseJsonFromResponse(tokenResponse)

    if (!tokenResponse.ok) {
      throw createAuthError(
        "Error on token post",
        jsonData,
        LoginError,
      )
    }

    this._accessToken = jsonData["access_token"]
    this._refreshToken = jsonData["refresh_token"]
    this._idToken = jsonData["id_token"]

    const expiresIn = jsonData["expires_in"]
    this.accessExpires = moment().unix() + _.toInteger(expiresIn)

    // nonce verification
    const decodedToken = parseJwt(this._idToken)
    const nonce = decodedToken["nonce"]
    const savedNonce = getFromLocalStorage(NONCE_KEY)
    if (nonce !== savedNonce) {
      throw new IDTokenInvalidNonce()
    }

    this.saveTokenDataToLocalStorage()

    removeFromLocalStorage(CODE_VERIFIER_KEY)
    removeFromLocalStorage(STATE_KEY)
    removeFromLocalStorage(NONCE_KEY)

    return jsonData
  }

  // High level methods
  // Auth methods
  async login(usernameEmail, password) {
    let basic_data = `${usernameEmail}:${password}`
    basic_data = btoa(basic_data)

    const authorization = `Basic ${basic_data}`

    const response = await this.postRequest(
      "/login/",
      {},
      false,
      "application/json",
      {
        "Authorization": authorization,
      },
    )

    if (!response.ok) {
      if (response.status === 400) {
        let message = "Erro ao fazer o login com as credenciais fornecidas"
        throw new LoginError({message})
      } else {
        let message = "Erro de conexão com servidor de login"
        throw new ConnectionError({message})
      }
    }

    let respData = await response.json()
    respData = respData.data

    this._accessToken = respData.str_access_token
    this._refreshToken = respData.str_refresh_token

    this.accessExpires = Date.parse(respData.dh_access_expires_at)
    this.refreshExpires = Date.parse(respData.dh_refresh_expires_at)

    this.saveTokenDataToLocalStorage()

    return response
  }

  async logout() {
    const response = await this.postRequest(
      "/logout/",
      {
        "refresh_token": getFromLocalStorage(REFRESH_TOKEN_KEY),
      },
    )

    if (!response.ok) {
      throw new TokenInvalidError(await response.json(), "Bad token on logout")
    }

    this.cleanTokenDataFromLocalStorage()
  }

  async verifyAuthSession() {
    const response = await this.getRequest(
      "/validate-session-code/",
      false,
      "application/json",
    )

    if (!response.ok) {
      throw new InvalidSessionError("Invalid session error")
    }
  }

  // User methods
  async updateUser(dataToUpdate) {
    if (this.isAccessTokenExpired()) {
      throw new TokenExpiredError()
    }

    const response = await this.putRequest(
      "/users/update-user/", dataToUpdate,
    )

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new TokenInvalidError(await response.json(), "Bad token")
      } else {
        throw new BadRequest(await response.json(), "Bad request")
      }
    }

    return await response.json()
  }

  async whoami() {
    if (this.isAccessTokenExpired()) {
      throw new TokenExpiredError()
    }

    const response = await this.getRequest("/users/whoami/")

    if (!response.ok) {
      throw new TokenInvalidError(
        await response.json(),
        "Invalid auth information",
      )
    }

    return await response.json()
  }

  async requestResetPassword(strEmail) {
    const response = await this.postRequest(
      "/users/request-reset-password/",
      {
        str_email: strEmail,
      },
      false,
    )

    if (!response.ok) {
      throw new BadRequest(await response.json(), "Bad request")
    }

    return await response.json()
  }

  async requestResendToken(strEmail) {
    const response = await this.postRequest(
      "/users/resend-token/",
      {
        str_email: strEmail,
      },
      false,
    )

    if (!response.ok) {
      throw new BadRequest(await response.json(), "Bad request")
    }

    return await response.json()
  }

  async resetPassword(strToken, strNewPassword) {
    const response = await this.postRequest(
      "/users/reset-password/",
      {
        str_new_password: strNewPassword,
        str_token: strToken,
      },
      false,
    )

    if (!response.ok) {
      throw new BadRequest(await response.json(), "Bad request")
    }

    return await response.json()
  }

  async verifyEmail(strToken) {
    const response = await this.postRequest(
      "/users/verify-email/",
      {
        str_token: strToken,
      },
      false,
    )

    if (!response.ok) {
      throw new BadRequest(await response.json(), "Bad request")
    }

    return await response.json()
  }

  async changePassword(strOldPassword, strNewPassword) {
    const response = await this.putRequest(
      "/users/complete-register/",
      {
        str_old_password: strOldPassword,
        str_new_password: strNewPassword,
      })

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new TokenInvalidError(await response.json(), "Bad token")
      } else {
        throw new BadRequest(await response.json(), "Bad request")
      }
    }

    return await response.json()
  }

  async changePasswordNew(strOldPassword, strNewPassword) {
    const response = await this.putRequest(
      "/users/change-password/",
      {
        str_old_password: strOldPassword,
        str_new_password: strNewPassword,
      })

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new TokenInvalidError(await response.json(), "Bad token")
      } else {
        throw new BadRequest(await response.json(), "Bad request")
      }
    }

    return await response.json()
  }  

  async createReferralToken() {
    const response = await this.postRequest(
      "/users/create-referral-token/",
    )

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new TokenInvalidError(await response.json(), "Bad token")
      } else {
        throw new BadRequest(await response.json(), "Bad request")
      }
    }

    return await response.json()
  }

  loadAuthenticationInfo() {
    this.loadTokenDataFromLocalStorage()
    return this.oauthIntrospectToken()
  }

  // Helpers methods
  getAuthToken(callbackNoAuth = true, shouldRaise = true) {
    if (!getFromLocalStorage(ACCESS_TOKEN_KEY)) {
      if (callbackNoAuth && this.onNeedAuthentication) {
        this.onNeedAuthentication()
      }

      if (shouldRaise) {
        throw new NoAccessTokenError("Access token is not available")
      }
    }

    return getFromLocalStorage(ACCESS_TOKEN_KEY)
  }

  createAuthHeader() {
    if (!getFromLocalStorage(ACCESS_TOKEN_KEY)) {
      throw new NoAccessTokenError("Access token is null")
    }

    const accessToken = this.getAuthToken(false, true)

    return `Bearer ${accessToken}`
  }

  postRequest(url, data = {}, withAuth = true, contentType = "application/json", headers = {}) {
    return this.requestAuth("POST", url, data, withAuth, contentType, headers)
  }

  getRequest(url, withAuth = true, contentType = "application/json", headers = {}) {
    return this.requestAuth("GET", url, {}, withAuth, contentType, headers)
  }

  putRequest(url, data = {}, withAuth = true, contentType = "application/json", headers = {}) {
    return this.requestAuth("PUT", url, data, withAuth, contentType, headers)
  }

  deleteRequest(url, data = {}, withAuth = true, contentType = "application/json", headers = {}) {
    return this.requestAuth("DELETE", url, data, withAuth, contentType, headers)
  }

  requestAuth(method, url, data = {}, withAuth = true, contentType = "application/json", headers = {}) {

    headers = {
      "Content-Type": contentType,
      ...headers,
    }

    if (withAuth) {
      headers["Authorization"] = this.createAuthHeader()
    }

    const finalUrl = this.createAuthUrl(url)

    const requestData = {
      method: method,
      headers: headers,
      redirect: "error",
      credentials: "include",
    }

    if (Object.keys(data).length !== 0) {
      requestData["body"] = JSON.stringify(data)
    }

    return fetch(finalUrl, requestData)
  }

  catchRequestHandler(err) {
    if (err instanceof TokenInvalidError || err instanceof TokenExpiredError) {
      if (this.onNeedAuthentication) {
        this.onNeedAuthentication()
      }
    } else {
      if (this.onNetworkError) {
        this.onNetworkError()
      }
    }
  }

  isAccessTokenExpired() {
    if (!getFromLocalStorage(ACCESS_TOKEN_EXP_KEY)) {
      return true
    }

    return Math.floor(new Date().getTime() / 1000) > _.toInteger(getFromLocalStorage(ACCESS_TOKEN_EXP_KEY))
  }

  saveTokenDataToLocalStorage() {
    saveToLocalStorage(ACCESS_TOKEN_KEY, this._accessToken)
    saveToLocalStorage(REFRESH_TOKEN_KEY, this._refreshToken)
    saveToLocalStorage(ID_TOKEN_KEY, this._idToken)

    saveToLocalStorage(ACCESS_TOKEN_EXP_KEY, this.accessExpires)
  }

  loadTokenDataFromLocalStorage() {
    this._accessToken = getFromLocalStorage(ACCESS_TOKEN_KEY)
    this._refreshToken = getFromLocalStorage(REFRESH_TOKEN_KEY)
    this.accessExpires = getFromLocalStorage(ACCESS_TOKEN_EXP_KEY)
  }

  // noinspection JSMethodCanBeStatic
  cleanTokenDataFromLocalStorage() {
    removeFromLocalStorage(ACCESS_TOKEN_KEY)
    removeFromLocalStorage(REFRESH_TOKEN_KEY)
    removeFromLocalStorage(ACCESS_TOKEN_EXP_KEY)
  }

  createAuthUrl(url) {
    if (url.startsWith("/")) {
      url = url.substring(1, url.length)
    }

    return `${this.authServiceUrl}/${url}`
  }
}


